import { inject, Injectable } from '@angular/core';
import { ApiHandlerService } from './api-base.service';
import { SessionService } from './session.service';
import { map, Observable, switchMap, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HttpClient, HttpContext } from '@angular/common/http';
import { SKIP_HEADER_INTERCEPTOR } from '../interceptors';
import moment from 'moment';
import * as CryptoJS from 'crypto-js';
import {
  Lookups,
  LookupsRequest,
  ReportParamsRequest,
  ReportTemplate,
  Schedule,
  ScheduleInterval,
  SchedulesData,
} from '../interfaces';
import {
  DAY_OF_MONTH_OPTIONS,
  DAY_OF_WEEK_OPTIONS,
  REPORTS,
  TIME_OPTIONS,
} from '../constants';
import { environment } from 'environments/environment.default';

@Injectable({ providedIn: 'root' })
export class SendingService {
  apiHandlerService = inject(ApiHandlerService);
  sessionService = inject(SessionService);
  httpClient = inject(HttpClient);

  getSchedules(
    reportId: string,
    templateId: string,
    domainId: number
  ): Observable<SchedulesData> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_report_schedules',
      'ce'
    );

    const body = {
      p_report_params: {
        p_user_id: this.sessionService.sessionData?.Username,
        p_environment: this.sessionService.sessionData?.Environment,
        p_domains: [domainId],
        p_report_id: reportId,
        p_template_id: templateId,
        p_is_temporary: false,
      },
    };

    return this.apiHandlerService.Post(url, body);
  }

  getCredentialTokens(domainId: number): Observable<any> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'get_connection_tokens',
      'ce'
    );

    const body = {
      p_report_params: {
        p_domains: [domainId],
        p_param_name: ['airflow'],
      },
    };

    return this.apiHandlerService.Post(url, body).pipe(
      map((response: any) => {
        if (response?.error) {
          throw new Error(
            response.error.message ||
              'Failed to load google bucket credentials.'
          );
        }

        return {
          success: true,
          data: response,
        };
      }),
      catchError((err) => {
        const errorMessage =
          err?.message || 'Failed to load or parse google bucket credentials.';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  getRequestParamsToAirflow(
    template: ReportTemplate,
    formValue: any,
    reportBodyModel: any,
    scheduleInterval?: ScheduleInterval,
    currentSchedule?: Schedule | null
  ) {
    const { report_id, template_id, domain_id } = template;
    const {
      scheduleName,
      expirationDate,
      email,
      emailSubject,
      emailCC,
      emailBCC,
      destination,
      folderName,
      connectionId,
      fileName,
      dateTimeFormat,
    } = formValue;

    const notMailType = {
      connection_type: destination,
      connection_id: connectionId,
      folder_name: folderName,
      filename_format: fileName,
      timestamp_format: dateTimeFormat,
    };

    const report = Object.values(REPORTS).find((r) => r.reportId === report_id);

    const uuid = crypto.randomUUID();

    return {
      p_report_params: {
        p_connection_details:
          destination === 'email'
            ? {
                connection_type: destination,
                email_bcc: emailBCC,
                email_cc: emailCC,
                email_subject: emailSubject,
                email_to: email,
              }
            : notMailType,
        p_domains: [domain_id],
        p_expiration_date: expirationDate
          ? moment(expirationDate).local().format('YYYY-MM-DD')
          : moment().add(6, 'months').format('YYYY-MM-DD'),
        p_operation_type: currentSchedule ? '1' : '0',
        p_report_dates: [],
        p_report_id: report?.reportKey,
        p_report_endpoint: 'bqproxy',
        p_report_request_body: { p_report_params: reportBodyModel },
        p_report_tab_id: null,
        p_report_tab_name: null,
        p_schedule_desc: '',
        p_schedule_group_id: currentSchedule?.schedule_group_id || uuid,
        p_schedule_id: currentSchedule?.schedule_id || null,
        p_schedule_interval: scheduleInterval
          ? this.convertToCron(scheduleInterval)
          : null,
        p_schedule_name: scheduleName || report?.reportName || null,
        p_template_id: template_id,
        p_user_id: template.user_id,
      },
    };
  }

  getTimeStampOptions(domainId: number): Observable<Lookups[]> {
    const url = this.apiHandlerService.getACSBaseUrl(
      'rep_report_params_lookup',
      'ce'
    );
    const body = {
      p_report_params: {
        p_domains: [domainId],
        p_param_name: 'filename_timestamp_format',
        p_filters: {},
        p_environment: '',
      },
    };
    return this.apiHandlerService.Post<
      ReportParamsRequest<LookupsRequest>,
      Lookups[]
    >(url, body);
  }

  triggerAirflowJob(template: ReportTemplate, request: any, isOnce: boolean) {
    return this.getCredentialTokens(template.domain_id).pipe(
      switchMap((res) => {
        const encryptedCreds = res.data[0].tokens;
        const connection = res.data[0].connection;

        const bytes = CryptoJS.AES.decrypt(encryptedCreds, connection);
        const credsObj = bytes.toString(CryptoJS.enc.Utf8);
        const url = isOnce
          ? environment.AIRFLOW_ONCE_URL
          : environment.AIRFLOW_PERIODICALLY_URL;
        const headers = {
          'Content-Type': 'application/json',
          Authorization: credsObj,
        };

        const body = {
          conf: request,
          logical_date: moment().format(),
        };

        return this.httpClient.post<any>(url, body, {
          headers,
          context: new HttpContext().set(SKIP_HEADER_INTERCEPTOR, true),
        });
      })
    );
  }

  saveReportSchedule(
    template: ReportTemplate,
    formValue: any,
    reportBodyModel: any,
    scheduleInterval: ScheduleInterval,
    currentSchedule?: Schedule | null
  ) {
    const body = this.getRequestParamsToAirflow(
      template,
      formValue,
      reportBodyModel,
      scheduleInterval,
      currentSchedule
    );

    const url = this.apiHandlerService.getACSBaseUrl(
      'save_report_schedule',
      'ce'
    );

    return this.apiHandlerService.Post(url, body).pipe(
      map((response: any) => {
        const result = response?.[0];
        if (result && result.successful !== 'true') {
          throw new Error(result?.message || 'saveReportSchedule failed');
        }
        return response;
      })
    );
  }

  deleteSchedule(domainId: number, scheduleId: string) {
    const body = {
      p_report_params: {
        p_domains: [domainId],
        p_operation_type: '2',
        p_schedule_group_id: null,
        p_schedule_id: scheduleId,
      },
    };

    const url = this.apiHandlerService.getACSBaseUrl(
      'save_report_schedule',
      'ce'
    );

    return this.apiHandlerService.Post(url, body);
  }

  convertToCron(sendOptions: any) {
    const { schedulePeriod, weekDay, month, timeHours } = sendOptions;

    if (schedulePeriod === '0') {
      return `0 ${Number(timeHours.slice(0, 2))} * * *`;
    } else if (schedulePeriod === '1') {
      return `0 ${Number(timeHours.slice(0, 2))} * * ${Number(weekDay)}`;
    } else {
      return `0 ${Number(timeHours.slice(0, 2))} ${Number(month)} * *`;
    }
  }

  convertFromCronToDate(item: any) {
    const parsedArrCron =
      item && item.schedule_interval
        ? item.schedule_interval.split(' ')
        : ['*', '*', '*', '*', '*'];
    if (item.send_option_period === '0') {
      const setTimeOption =
        TIME_OPTIONS.find(
          (item) => Number(item.lookup_key.slice(0, 2)) == parsedArrCron[1]
        ) || TIME_OPTIONS[0];
      return { schedule_type: 'Daily', time_option: setTimeOption.lookup_key };
    } else if (item.send_option_period === '1') {
      const setTimeOption =
        TIME_OPTIONS.find(
          (item) => Number(item.lookup_key.slice(0, 2)) == parsedArrCron[1]
        ) || TIME_OPTIONS[0];
      const setDayWeekOption =
        DAY_OF_WEEK_OPTIONS.find(
          (item) => Number(item.lookup_key) == parsedArrCron[4]
        ) || DAY_OF_WEEK_OPTIONS[0];

      return {
        schedule_type: 'Weekly on ' + setDayWeekOption.lookup_value,
        time_option: setTimeOption.lookup_key,
        day_of_week: setDayWeekOption.lookup_key,
      };
    } else {
      const setTimeOption =
        TIME_OPTIONS.find(
          (item) => Number(item.lookup_key.slice(0, 2)) == parsedArrCron[1]
        ) || TIME_OPTIONS[0];
      const setDayOption =
        DAY_OF_MONTH_OPTIONS.find(
          (item) => Number(item.lookup_key) == parsedArrCron[2]
        ) || DAY_OF_MONTH_OPTIONS[0];
      return {
        schedule_type: 'Monthly on ' + setDayOption.lookup_value,
        time_option: setTimeOption.lookup_key,
        day_of_month: setDayOption.lookup_key,
      };
    }
  }

  transformToPeriodLookupKey(item: any) {
    const parsedArrCron = item.schedule_interval.split(' ');
    if (
      parsedArrCron[1] !== '*' &&
      parsedArrCron[2] === '*' &&
      parsedArrCron[4] === '*'
    ) {
      return '0';
    } else if (
      parsedArrCron[1] !== '*' &&
      parsedArrCron[2] === '*' &&
      parsedArrCron[4] !== '*'
    ) {
      return '1';
    } else if (
      parsedArrCron[1] !== '*' &&
      parsedArrCron[2] !== '*' &&
      parsedArrCron[4] === '*'
    ) {
      return '2';
    }
    return;
  }
}
